import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Users, Linkedin, Instagram, Facebook, ArrowRight } from 'lucide-react';

/*
  AuthorBlock — used on article page (sidebar / bottom) and author profile page.
  Role icon makes the AI nature of Penny/Sonny visible and trustworthy.
*/

const ROLE_ICON = {
  ai: Sparkles,
  team: Users,
  human: null,
};

const ROLE_LABEL = {
  ai: 'AI Operating Team',
  team: 'Editors',
  human: 'Contributor',
};

const SOCIAL_ICON = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook };

export default function AuthorBlock({ author, compact = false }) {
  if (!author) return null;
  const RoleIcon = ROLE_ICON[author.role] || null;

  if (compact) {
    return (
      <Link
        to="/ai-agents-for-restaurants"
        className="inline-flex items-center gap-2 group"
        data-testid={`magazine-author-${author.slug}`}
      >
        {author.avatar_url && (
          <img
            src={author.avatar_url}
            alt={author.name}
            className="w-8 h-8 rounded-full object-cover ring-1 ring-neutral-200"
            loading="lazy"
          />
        )}
        <span className="text-[13px] font-medium text-neutral-700 group-hover:text-[#F77F11] transition-colors">
          {author.name}
        </span>
      </Link>
    );
  }

  return (
    <div
      className="flex items-start gap-4 p-5 rounded-2xl border border-neutral-200 bg-neutral-50/60"
      data-testid={`magazine-author-block-${author.slug}`}
    >
      {author.avatar_url && (
        <img
          src={author.avatar_url}
          alt={author.name}
          className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
          loading="lazy"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link
            to="/ai-agents-for-restaurants"
            className="text-[15px] font-bold text-neutral-900 hover:text-[#F77F11] transition-colors tracking-[-0.005em]"
          >
            {author.name}
          </Link>
          {RoleIcon && (
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase font-bold text-[#F77F11] bg-orange-50 px-2 py-0.5 rounded-full">
              <RoleIcon className="w-3 h-3" />
              {ROLE_LABEL[author.role] || 'Author'}
            </span>
          )}
        </div>
        {author.title && (
          <p className="mt-0.5 text-[12px] tracking-[0.04em] text-neutral-500 font-medium">
            {author.title}
          </p>
        )}
        {author.bio && (
          <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">
            {author.bio}
          </p>
        )}
        {(Object.keys(author.socials || {}).length > 0 || author.specialty_href) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {Object.entries(author.socials || {}).map(([key, href]) => {
              const Icon = SOCIAL_ICON[key];
              return Icon ? (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer"
                  aria-label={`${author.name} on ${key}`} data-testid={`author-social-${author.slug}-${key}`}
                  className="text-neutral-400 hover:text-[#F77F11] transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ) : null;
            })}
            {author.specialty_href && (
              <Link to={author.specialty_href} data-testid={`author-specialty-${author.slug}`}
                className="inline-flex items-center gap-1 text-[12px] font-bold text-[#F77F11] hover:gap-2 transition-all">
                {author.specialty_label || 'Discover more'} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
